<template>
  <div class="c-tabs__wrapper">
    <div class="c-tabs__list-wrapper">
      <slot name="before-tablist" />
      <ul
        class="c-tabs"
        role="tablist"
        :aria-label="ariaLabel"
        v-shortkey="{
          left: ['arrowleft'],
          right: ['arrowright'],
          home: ['home'],
          end: ['end'],
        }"
        @shortkey="handleTabsKeyEvent"
      >
        <li
          v-for="tab in tabs"
          :key="tab.title"
          class="c-tabs__item"
          :class="{ 'c-tabs__item--disabled': tab.isDisabled }"
          role="presentation"
        >
          <FHButton
            v-html="tab.title"
            class="c-tabs__btn"
            role="tab"
            :ref="tab.hash"
            :disabled="tab.isDisabled"
            :aria-controls="tab.hash"
            :aria-selected="tab.isActive ? 'true' : 'false'"
            :class="{ 'c-tabs__btn--active': tab.isActive }"
            :tabindex="!tabable ? (tab.isActive ? '0' : '-1') : '0'"
            @click.native="setActive(tab.hash)"
          />
        </li>
      </ul>
      <slot name="after-tablist" />
    </div>

    <div class="c-tabs__panels-wrapper">
      <slot name="before-panels" />
      <div class="c-tabs__panels">
        <slot />
      </div>
      <slot name="after-panels" />
    </div>
  </div>
</template>

<script>
import FHButton from '@forlagshuset/v-button'

export default {
  name: 'fh-tabs',

  components: {
    FHButton,
  },

  data() {
    return {
      tabs: [],
      activeTab: '',
      activeTabHash: '',
      activeTabIndex: 0,
    }
  },

  props: {
    ariaLabel: String,
    tabable: {
      type: Boolean,
      default: false,
    },
    initialTab: String,
  },
  mounted() {
    const tabs = this.$children.map(child => {
      if (child.constructor.options.name === 'fh-tab') {
        return child
      }
    })
    this.tabs = tabs
    if (this.tabs.length) {
      this.initialTab ? this.setActive(`#${this.initialTab}`) : this.firstTab()
    }
  },

  methods: {
    findTab(hash) {
      return this.tabs.find(tab => tab.hash === hash)
    },

    checkActiveTab(tabName) {
      return this.activeTab === tabName
    },

    setActive(hash) {
      const selectedTab = this.findTab(hash)

      if (!selectedTab) return
      if (selectedTab.isDisabled) return

      this.tabs.forEach(tab => {
        tab.isActive = tab.hash === selectedTab.hash
      })
      this.$emit('changed', { tab: selectedTab })
      this.activeTabHash = selectedTab.hash
      this.activeTabIndex = this.getTabIndex(selectedTab.hash)
      this.tabs.forEach(tab => {
        if (this.$refs[tab.hash]) {
          this.$refs[tab.hash][0].$el.blur()
        }
      })
    },

    handleTabsKeyEvent(ev) {
      if (
        this.activeTabHash.length === 0 ||
        this.$refs[this.activeTabHash][0].$el !== document.activeElement
      ) {
        return
      }

      switch (ev.srcKey) {
        case 'left':
          this.previousTab()
          break
        case 'right':
          this.nextTab()
          break
        case 'home':
          this.firstTab()
          break
        case 'end':
          this.lastTab()
          break
        default:
      }
      this.$refs[this.activeTabHash][0].$el.focus()
    },

    nextTab() {
      for (var i = this.activeTabIndex + 1; i < this.tabs.length; i++) {
        if (!this.tabs[i].isDisabled) {
          this.setActive(this.tabs[i].hash)
          return
        }
      }
    },

    previousTab() {
      for (var i = this.activeTabIndex - 1; i >= 0; i--) {
        if (!this.tabs[i].isDisabled) {
          this.setActive(this.tabs[i].hash)
          return
        }
      }
    },

    firstTab() {
      for (var i = 0; i < this.tabs.length; i++) {
        if (!this.tabs[i].isDisabled) {
          this.setActive(this.tabs[i].hash)
          return
        }
      }
    },

    lastTab() {
      for (var i = this.tabs.length - 1; i >= 0; i--) {
        if (!this.tabs[i].isDisabled) {
          this.setActive(this.tabs[i].hash)
          return
        }
      }
    },

    getTabIndex(hash) {
      const tab = this.findTab(hash)

      return this.tabs.indexOf(tab)
    },
  },
}
</script>

<style lang="scss">
.c-tabs {
  list-style: none none;
  margin: 0;
  padding: 0;
  display: flex;
}

.c-tabs__item {
  display: inline-flex;
}

.c-tabs__btn {
  border: 1px solid #333;

  &--active {
    background: #333;
    color: #fff;
  }
}
</style>
